import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import Layout from "@/components/Layout";
import PageHeader from "@/components/PageHeader";
import Reveal from "@/components/Reveal";
import { toast } from "sonner";
import { submitContact } from "@/lib/api";

const TOPICS = [
  { value: "acquisition", label: "Acquisition" },
  { value: "exhibition", label: "Exhibition" },
  { value: "press", label: "Press" },
  { value: "support", label: "Support the estate" },
  { value: "general", label: "General" },
];

export default function Contact() {
  const [params] = useSearchParams();
  const initialTopic = params.get("topic") || "general";
  const [topic, setTopic] = useState(initialTopic);
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [subject, setSubject] = useState("");
  const [message, setMessage] = useState("");
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);

  useEffect(() => {
    setTopic(initialTopic);
  }, [initialTopic]);

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!name.trim() || !email.trim() || !message.trim()) {
      toast("Please share your name, email, and a note.");
      return;
    }
    setSending(true);
    try {
      await submitContact({
        name: name.trim(),
        email: email.trim(),
        subject: subject.trim() || null,
        message: message.trim(),
        topic,
      });
      setSent(true);
    } catch {
      toast("We couldn't send your note. Please try again.");
    } finally {
      setSending(false);
    }
  };

  return (
    <Layout>
      <main data-testid="contact-page" className="bg-[color:var(--bone)]">
        <PageHeader
          eyebrow="Contact"
          title="Write to the estate."
          lede="For acquisitions, exhibitions, press, or simply to say hello. We answer personally, usually within a week."
          image="/release/tribal.jpg"
        />

        <section className="bone-section py-24 md:py-40">
          <div className="max-w-[1500px] mx-auto px-6 md:px-12 grid grid-cols-1 md:grid-cols-12 gap-12 md:gap-24">
            <Reveal className="md:col-span-5">
              <p className="hairline opacity-60 mb-4">Write to the estate</p>
              <ul className="space-y-7 font-serif italic text-xl md:text-2xl">
                <li>
                  <span className="hairline opacity-50 block mb-1">Email</span>
                  <a href="mailto:info@nickfraserartist.com" className="link-quiet">
                    info@nickfraserartist.com
                  </a>
                </li>
                <li>
                  <span className="hairline opacity-50 block mb-1">Website</span>
                  <a
                    href="https://www.nickfraserartist.com"
                    target="_blank"
                    rel="noreferrer"
                    className="link-quiet"
                  >
                    nickfraserartist.com
                  </a>
                </li>
              </ul>

              <div className="rule my-12 opacity-30" />

              <p className="hairline opacity-60 mb-3">Studio</p>
              <p className="font-serif italic text-xl opacity-80 leading-relaxed">
                The estate is administered from London,
                <br />
                by Mark &amp; Marcus O'Shaughnessy.
              </p>
            </Reveal>

            <div className="md:col-span-7">
              {sent ? (
                <Reveal data-testid="contact-success">
                  <p className="hairline opacity-60 mb-6">Received</p>
                  <h2 className="font-serif italic text-4xl md:text-6xl leading-[1.05]">
                    Thank you.
                  </h2>
                  <p className="font-serif italic text-xl md:text-2xl opacity-75 mt-6 max-w-lg">
                    Your note is with us. We reply personally — usually within
                    a week, sometimes sooner.
                  </p>
                </Reveal>
              ) : (
                <Reveal>
                  <form
                    onSubmit={onSubmit}
                    className="space-y-9"
                    data-testid="contact-form"
                  >
                    <div>
                      <label className="hairline opacity-60 block mb-3">
                        Topic
                      </label>
                      <div className="flex flex-wrap gap-x-6 gap-y-3">
                        {TOPICS.map((t) => (
                          <button
                            key={t.value}
                            type="button"
                            onClick={() => setTopic(t.value)}
                            className={`hairline transition-opacity duration-500 ${
                              topic === t.value
                                ? "opacity-100 border-b border-[color:var(--ink)] pb-1"
                                : "opacity-50 hover:opacity-100"
                            }`}
                            data-testid={`contact-topic-${t.value}`}
                          >
                            {t.label}
                          </button>
                        ))}
                      </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                      <div>
                        <label className="hairline opacity-60 block mb-2">
                          Name
                        </label>
                        <input
                          type="text"
                          value={name}
                          onChange={(e) => setName(e.target.value)}
                          className="invisible-input"
                          data-testid="contact-name-input"
                          autoComplete="name"
                        />
                      </div>
                      <div>
                        <label className="hairline opacity-60 block mb-2">
                          Email
                        </label>
                        <input
                          type="email"
                          value={email}
                          onChange={(e) => setEmail(e.target.value)}
                          className="invisible-input"
                          data-testid="contact-email-input"
                          autoComplete="email"
                        />
                      </div>
                    </div>

                    <div>
                      <label className="hairline opacity-60 block mb-2">
                        Subject (optional)
                      </label>
                      <input
                        type="text"
                        value={subject}
                        onChange={(e) => setSubject(e.target.value)}
                        className="invisible-input"
                        data-testid="contact-subject-input"
                      />
                    </div>

                    <div>
                      <label className="hairline opacity-60 block mb-2">
                        Message
                      </label>
                      <textarea
                        value={message}
                        onChange={(e) => setMessage(e.target.value)}
                        rows={6}
                        className="invisible-input resize-none"
                        data-testid="contact-message-input"
                      />
                    </div>

                    <div className="flex justify-end pt-2">
                      <button
                        type="submit"
                        disabled={sending}
                        className="link-quiet font-serif italic text-xl disabled:opacity-40"
                        data-testid="contact-submit-button"
                      >
                        {sending ? "Sending…" : "Send"}
                      </button>
                    </div>
                  </form>
                </Reveal>
              )}
            </div>
          </div>
        </section>
      </main>
    </Layout>
  );
}
